import { highnote } from "@highnote/server/sdk";
import { TextBlock } from "./TextBlock";
import { FileBlock } from "./FileBlock";
import { PollBlock } from "./PollBlock";
import { ReactComponent as VoiceIcon } from "App/common/icons/voice.svg";
import { ReactComponent as StickerIcon } from "App/common/icons-v2/sticker.svg";
import { ReactComponent as PollIcon } from "App/common/icons/poll.svg";
import { ReactComponent as TextIcon } from "App/common/icons/text.svg";
import React from "react";
import { PollReplyBlock } from "./PollReplyBlock";
import {
  Comment,
  CommentBlock,
  FileBlockV2,
  PollBlockV2,
  StickerBlock as StickerBlockType,
} from "@highnote/server/src/core/entities";
import { STICKER_IDS, StickerBlock } from "./StickerBlock";
import { ViewingSpaceId } from "App/modules/viewingSpace";

export type CommentBlockConfig<T> = {
  id: string;
  displayName: string;
  Component?: React.FC<{
    id?: string;
    replies?: Comment[];
    /** The VIEWING space — see App/modules/viewingSpace.ts. */
    spaceId?: ViewingSpaceId;
    timestamp?: number;
    createdBy?: string;
    createdByAlias?: string;
    isEditing?: boolean;
    data: T;
    onChange?: (block: T) => void;
    onSave?: (block: T) => void;
  }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon?: React.FC<any>;
  generateNew: (props?: Omit<T, "type">) => T;
  blockProps?: Omit<CommentBlock, "type">;
  isEditable: boolean;
  validate: (data: T) => string | undefined;
  inToolbar: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const COMMENT_BLOCKS: Record<string, CommentBlockConfig<any>> = {
  text: {
    id: "text",
    displayName: "Text",
    Component: TextBlock,
    Icon: TextIcon,
    generateNew: () => highnote.utils.generateTextBlock(),
    isEditable: true,
    inToolbar: true,
    validate: (block: TextBlock) => {
      if (typeof block.text !== "string" || !block.text.trim()) {
        return "Text must not be empty.";
      }
      return undefined;
    },
  } as CommentBlockConfig<TextBlock>,
  sticker: {
    id: "sticker",
    displayName: "Sticker",
    Component: StickerBlock,
    Icon: StickerIcon,
    generateNew: (props: Omit<StickerBlockType, "type">) =>
      highnote.utils.generateStickerBlock(props),
    isEditable: false,
    inToolbar: true,
    validate: (block: StickerBlockType) => {
      if (Boolean(block.id) && !STICKER_IDS[block.id]) {
        return "Invalid sticker id.";
      }

      if (!block.imageUrl) {
        return "Sticker must have an image URL.";
      }
      return undefined;
    },
  } as CommentBlockConfig<CommentBlock>,
  "file-v2": {
    id: "file-v2",
    displayName: "Voice",
    Component: FileBlock,
    Icon: VoiceIcon,
    generateNew: () => highnote.utils.generateFileBlock(),
    isEditable: true,
    inToolbar: true,
    validate: (block: FileBlockV2) => {
      if (!block.file) {
        return "Valid file required.";
      }
      return undefined;
    },
  } as CommentBlockConfig<FileBlockV2>,
  "poll-v2": {
    id: "poll-v2",
    displayName: "Poll",
    Component: PollBlock,
    generateNew: () => highnote.utils.generatePollBlock(),
    Icon: PollIcon,
    isEditable: true,
    inToolbar: true,
    validate: (block: PollBlockV2) => {
      if (!block.options || block.options.length !== 2) {
        return "Two poll options required.";
      }
      if (block.options.find((o) => !o.text && !(o as FileBlockV2).file)) {
        return "Every poll option must have either text or a file attached";
      }
      return undefined;
    },
  } as CommentBlockConfig<PollBlockV2>,
  "poll-reply": {
    id: "poll-reply",
    displayName: "Poll Reply",
    Component: PollReplyBlock,
    generateNew: () => highnote.utils.generatePollReplyBlock(),
    Icon: PollIcon,
    isEditable: false,
    inToolbar: false,
    validate: (block: PollReplyBlock) => {
      if (block.option === undefined) {
        return "Every poll reply must specify a selected option.";
      }
      return undefined;
    },
  } as CommentBlockConfig<PollReplyBlock>,
};
